import axios from "axios";
import { UseFormSetError } from "react-hook-form";

import { AlertTypes } from "./atoms";

export type RegisterErrorType =
  | "EMAIL_ALREADY_EXISTS"
  | "INVALID_EMAIL_FORMAT"
  | "SERVER_ERROR"
  | "NETWORK_ERROR"
  | "UNKNOWN_ERROR";

export const REGISTER_ERROR_PATTERNS = {
  EMAIL_ALREADY_EXISTS: "CscLogin already exists",
  INVALID_EMAIL_FORMAT: "Invalid Email Address",
} as const;

export const REGISTER_ERROR_MESSAGES = {
  EMAIL_ALREADY_EXISTS:
    "translation:newWelcome.registerForm.error.emailAlreadyInUse",
  INVALID_EMAIL_FORMAT:
    "translation:newWelcome.registerForm.error.invalidEmailFormat",
  SERVER_ERROR: "translation:Common.generalForm.error.genericError",
  NETWORK_ERROR: "translation:Common.generalForm.error.networkError",
  UNKNOWN_ERROR: "translation:Common.generalForm.error.genericError",
} as const;

export const determineRegisterErrorType = (
  error: unknown,
): RegisterErrorType => {
  // Handle Axios errors
  if (axios.isAxiosError(error)) {
    // Network errors (no response)
    if (error.code === "ECONNABORTED" || !error.response) {
      return "NETWORK_ERROR";
    }

    const status = error.response.status;
    const errorData = error.response.data;
    const errorMessage =
      typeof errorData === "string"
        ? errorData.toLowerCase()
        : typeof errorData?.message === "string"
          ? errorData.message.toLowerCase()
          : "";

    if (status === 400) {
      if (
        errorMessage.includes(
          REGISTER_ERROR_PATTERNS.EMAIL_ALREADY_EXISTS.toLowerCase(),
        )
      ) {
        return "EMAIL_ALREADY_EXISTS";
      }
    }

    if (status === 500) {
      // Check for invalid email format error from backend
      if (
        errorMessage.includes(
          REGISTER_ERROR_PATTERNS.INVALID_EMAIL_FORMAT.toLowerCase(),
        )
      ) {
        return "INVALID_EMAIL_FORMAT";
      }

      return "SERVER_ERROR";
    }
  }

  const possibleRegisterError = error as {
    status?: number;
    message?: string;
    type?: RegisterErrorType;
  };
  if (possibleRegisterError.type) {
    return possibleRegisterError.type;
  }

  if (possibleRegisterError.status === 400 && possibleRegisterError.message) {
    const message = possibleRegisterError.message.toLowerCase();
    if (
      message.includes(
        REGISTER_ERROR_PATTERNS.EMAIL_ALREADY_EXISTS.toLowerCase(),
      )
    ) {
      return "EMAIL_ALREADY_EXISTS";
    }
  }

  if (possibleRegisterError.status === 500) {
    return "SERVER_ERROR";
  }

  return "UNKNOWN_ERROR";
};

// Main registration error handler function
export const createRegisterErrorHandler = (
  setError: UseFormSetError<any>,
  t: (key: string) => string,
  showAlert: (alert: { text: string; type: any }) => void,
  emailFieldName: string = "emailAddress",
) => {
  return (error: unknown) => {
    const errorType = determineRegisterErrorType(error);

    switch (errorType) {
      case "EMAIL_ALREADY_EXISTS":
        setError(emailFieldName, {
          type: "manual",
          message: t(REGISTER_ERROR_MESSAGES.EMAIL_ALREADY_EXISTS),
        });
        break;

      case "INVALID_EMAIL_FORMAT":
        setError(emailFieldName, {
          type: "manual",
          message: t(REGISTER_ERROR_MESSAGES.INVALID_EMAIL_FORMAT),
        });
        break;

      case "NETWORK_ERROR":
        showAlert({
          text: t(REGISTER_ERROR_MESSAGES.NETWORK_ERROR),
          type: AlertTypes.error,
        });
        break;

      case "SERVER_ERROR":
        showAlert({
          text: t(REGISTER_ERROR_MESSAGES.SERVER_ERROR),
          type: AlertTypes.error,
        });
        break;

      default:
        showAlert({
          text: t(REGISTER_ERROR_MESSAGES.UNKNOWN_ERROR),
          type: AlertTypes.error,
        });
        break;
    }
  };
};
